import { ethers } from "ethers";
import { computeCommitment, computeNPK } from "../joinsplit/keys";
import { NoteTokenType, type JoinSplitNote } from "../joinsplit/types";

export const BN254_SCALAR_FIELD =
  21888242871839275222246405745257275088548364400416034343698204186575808495617n;

export function flatFieldHex(value: bigint): `0x${string}` {
  if (value < 0n || value >= BN254_SCALAR_FIELD) {
    throw new Error("Value is outside the BN254 scalar field");
  }
  return `0x${value.toString(16).padStart(64, "0")}`;
}

function parseTokenHash(value: string | bigint): bigint {
  const parsed = typeof value === "bigint" ? value : BigInt(value);
  if (parsed <= 0n || parsed >= BN254_SCALAR_FIELD) {
    throw new Error(
      "Flat JoinSplit token hash must be a nonzero field element",
    );
  }
  return parsed;
}

function randomFieldElement(): bigint {
  while (true) {
    const candidate =
      BigInt(ethers.hexlify(ethers.randomBytes(32))) % BN254_SCALAR_FIELD;
    if (candidate !== 0n) return candidate;
  }
}

export function flatTreeKey(
  chainId: number | bigint,
  poolAddress: string,
  treeIndex: number | bigint,
): string {
  const pool = ethers.getAddress(poolAddress).toLowerCase();
  return `flat-joinsplit-v2:${BigInt(chainId)}:${pool}:${BigInt(treeIndex)}`;
}

/** Create a note only from the pool-derived token domain for its target tree. */
export async function createFlatJoinSplitV2Note(params: {
  recipientMpk: bigint;
  chainId: number | bigint;
  poolAddress: string;
  treeIndex: number | bigint;
  tokenAddress: string;
  tokenHash: string | bigint;
  value: bigint;
  /**
   * Supply the blinding factor instead of generating one. Used where the note
   * key had to be chosen before its value was known — a private swap commits
   * to `npk` at proving time and only learns the amount at settlement — so the
   * completed note must reuse the same `random` or its commitment will not be
   * the one the pool inserted.
   */
  random?: bigint;
}): Promise<JoinSplitNote> {
  if (params.recipientMpk <= 0n || params.recipientMpk >= BN254_SCALAR_FIELD) {
    throw new Error("Recipient MPK is outside the BN254 scalar field");
  }
  if (params.value <= 0n || params.value >= 1n << 120n) {
    throw new Error(
      "Flat JoinSplit note value must fit the nonzero uint120 range",
    );
  }
  const chainId = Number(params.chainId);
  const treeIndex = Number(params.treeIndex);
  if (!Number.isSafeInteger(chainId) || chainId <= 0) {
    throw new Error("Invalid note chain ID");
  }
  if (!Number.isSafeInteger(treeIndex) || treeIndex < 0) {
    throw new Error("Invalid note tree index");
  }
  const poolAddress = ethers.getAddress(params.poolAddress);
  const tokenAddress = ethers.getAddress(params.tokenAddress);
  const tokenHash = parseTokenHash(params.tokenHash);
  const random = params.random ?? randomFieldElement();
  if (random <= 0n || random >= BN254_SCALAR_FIELD) {
    throw new Error("Note blinding factor is outside the BN254 scalar field");
  }
  const npk = await computeNPK(params.recipientMpk, random);
  const commitment = await computeCommitment(npk, tokenHash, params.value);
  return {
    schemaVersion: 2,
    protocol: "flat-joinsplit-v2",
    chainId,
    poolAddress,
    npk,
    random,
    commitment,
    tokenHash,
    tokenData: {
      tokenAddress,
      tokenType: NoteTokenType.ERC20,
      tokenSubID: "0",
    },
    value: params.value,
    leafIndex: -1,
    treeIndex,
    treeKey: flatTreeKey(chainId, poolAddress, treeIndex),
  };
}

/**
 * Authenticate all encrypted-note fields before the record is considered
 * spendable. Merkle membership and nullifier state are checked separately.
 */
export async function assertFlatJoinSplitV2Note(params: {
  note: JoinSplitNote;
  chainId: number | bigint;
  poolAddress: string;
  tokenAddress?: string;
  expectedTokenHash?: string | bigint;
  expectedMpk?: bigint;
}): Promise<void> {
  const { note } = params;
  const chainId = Number(params.chainId);
  const pool = ethers.getAddress(params.poolAddress);
  if (
    note.schemaVersion !== 2 ||
    note.protocol !== "flat-joinsplit-v2" ||
    note.chainId !== chainId ||
    !note.poolAddress ||
    ethers.getAddress(note.poolAddress) !== pool
  ) {
    throw new Error("Encrypted note belongs to a different protocol namespace");
  }
  if (
    note.treeIndex === undefined ||
    !Number.isSafeInteger(note.treeIndex) ||
    note.treeIndex < 0 ||
    note.treeKey !== flatTreeKey(chainId, pool, note.treeIndex)
  ) {
    throw new Error("Encrypted note has invalid tree metadata");
  }
  if (note.value <= 0n || note.value >= 1n << 120n) {
    throw new Error("Encrypted note has an invalid value");
  }
  const tokenHash = parseTokenHash(note.tokenHash);
  if (
    params.expectedTokenHash !== undefined &&
    tokenHash !== parseTokenHash(params.expectedTokenHash)
  ) {
    throw new Error("Encrypted note token domain does not match the pool");
  }
  if (params.tokenAddress) {
    if (
      !note.tokenData ||
      note.tokenData.tokenType !== NoteTokenType.ERC20 ||
      BigInt(note.tokenData.tokenSubID || "0") !== 0n ||
      ethers.getAddress(note.tokenData.tokenAddress) !==
        ethers.getAddress(params.tokenAddress)
    ) {
      throw new Error(
        "Encrypted note token identity does not match the action",
      );
    }
  }
  if (
    note.npk <= 0n ||
    note.npk >= BN254_SCALAR_FIELD ||
    note.random < 0n ||
    note.random >= BN254_SCALAR_FIELD
  ) {
    throw new Error("Encrypted note contains a non-canonical field element");
  }
  if (params.expectedMpk !== undefined) {
    const expectedNpk = await computeNPK(params.expectedMpk, note.random);
    if (expectedNpk !== note.npk) {
      throw new Error(
        "Encrypted note is not owned by the connected privacy key",
      );
    }
  }
  const expectedCommitment = await computeCommitment(
    note.npk,
    tokenHash,
    note.value,
  );
  if (expectedCommitment.toLowerCase() !== note.commitment.toLowerCase()) {
    throw new Error("Encrypted note commitment does not match its plaintext");
  }
}
